import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  Button,
  Heading,
  Modal,
  ModalPortal,
  RadiusToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import autoassignmentPromo from "assets/img/auto-assignment-promo@2x.png";
import Flex from "components/layout/Flex";
import { newFeaturesCampaigns } from "constants/campaigns";
import { HelpCenter } from "constants/help-center";
import { trackEngagement } from "helpers/analytics";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { selectIsAdmin } from "store/entities/agents";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

export const AutoAssignmentPromo: FC = () => {
  const isAdmin = useSelector(selectIsAdmin);
  const {
    featureFlagValue: hasClosed,
    isNewForAgent,
    setAgentFlagCallback: markAsClosed,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasClosedAutoAssignmentPromo.flag,
    newFeaturesCampaigns.hasClosedAutoAssignmentPromo.campaignStartDate
  );

  const shouldShow = !hasClosed && isNewForAgent && isAdmin;

  const [isVisible, setIsVisible] = useState(shouldShow);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleClose = () => {
    setIsVisible(false);
    markAsClosed();
  };

  if (!shouldShow) {
    return null;
  }

  return (
    isVisible && (
      <ModalPortal zIndex={StackingContextLevel.Modal}>
        <AutoAssignmentPromoModal onClose={handleClose} />
      </ModalPortal>
    )
  );
};

interface IProps {
  onClose: () => void;
}

const AutoAssignmentPromoModal: FC<IProps> = ({ onClose }) => {
  useEffect(() => {
    trackEngagement("Agents: Autoassignment promo modal shown");
  }, []);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleLearnMore = () => {
    trackEngagement("Agents: Autoassignment promo modal learn more clicked");
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleClose = () => {
    trackEngagement("Agents: Autoassignment promo modal closed");
    onClose();
  };

  return (
    <Modal fullSpaceContent closeOnOverlayPress={false} onClose={handleClose}>
      <Flex column maxWidth="600px" gap="32px" alignItems="center" paddingBottom={withVar(SpacingToken.Spacing16)}>
        <img
          src={autoassignmentPromo}
          alt="Auto-assignment promo"
          css={{
            maxWidth: "100%",
            borderTopLeftRadius: withVar(RadiusToken[2]),
            borderTopRightRadius: withVar(RadiusToken[2]),
          }}
        />
        <Content column paddingH="20px">
          <Heading>Advanced auto-assignment settings now available 🎉</Heading>
          <Text>
            Set up auto-assignment settings for individual agents to balance their workloads. Activate auto-assignment
            when agents are available and deactivate it on the days off.
          </Text>
        </Content>
        <Flex gap={withVar(SpacingToken.Spacing2)}>
          <Button
            size="large"
            kind="secondary"
            onClick={handleLearnMore}
            href={HelpCenter.AUTO_ASSIGNMENTS}
            target="_blank"
          >
            Learn more
          </Button>
          <Button size="large" kind="primary" onClick={handleClose}>
            Got it
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

const Content = styled(Flex)`
  gap: ${withVar(SpacingToken.Spacing3)};
  max-width: 80%;
  text-align: center;
  text-wrap: pretty;
`;
